import "react-datetime-picker/dist/DateTimePicker.css";

import moment from "moment-timezone";
import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import LoadingContainer from "src/components/LoadingContainer";
import { MAXIMO_TIME_ZONE } from "src/config/сonstants";
import { useModal } from "src/hooks/useModal";
import { setWorkRequestMessage } from "src/redux/actions/workRequest";
import { getUserData } from "src/redux/selectors/user";
import { FirebaseDataService } from "src/services/Firebase/data";
import {
  Client,
  DBLocation,
  IServiceTypeQuestion,
  MaximoDocument,
  PriorityLevel,
  PriorityLevelList,
  Ticket,
  ServiceTypeItem,
  ServiceTypeList,
  WorkType,
} from "src/types";
import { HighRiskClassificationOption } from "src/utils/serviceTypes";

import { useForm } from "react-hook-form";
import { AssignWOResourceFormValues } from "src/components/molecules/AssignWOResource/types";
import { setPageNameHeader } from "src/redux/actions/header";
import { CustomWorkRequestFieldList, FormData, TicketFormProps } from "./types";
import WorkRequestModals from "./components/WorkRequestModals";
import {
  clearWorkRequestMessageTimeout,
  fetchData,
  getButtonDisableCondition,
  getDropdownClassification,
  getServiceTypeQuestions,
  getTimeZone,
  saveCreateWorkOrderComment,
  onSubmitClientForm,
  onSubmitManagerForm,
  onSubmitNoAuthForm,
  uploadImages,
} from "./handler";
import ManagerCreateFormFields from "./components/ManagerCreateFormFields";
import NoAuthCreateFormFields from "./components/NoAuthCreateFormFields";
import { WORK_REQUEST_TYPE } from "src/pages/WorkRequests/constants";

export const CreateForm = ({
  setStep,
  isNoAuthMode,
  noAuthData,
  setSuccessMessage,
  isClient,
  isManager,
}: TicketFormProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const dispatch = useDispatch();

  const form = useForm();

  const {
    id: clientUserId,
    firstName,
    lastName,
    phoneNumber,
    email,
    clientId: clientPortalId,
    maximoTimezone: maximoTimezoneDef,
  } = useSelector(getUserData);
  const [clientId, setClientId] = useState(clientPortalId || "");

  const maximoTimezone = maximoTimezoneDef || MAXIMO_TIME_ZONE;
  const defaultServiceType = location?.state?.serviceType;
  const [workTypes, setWorkTypes] = useState<WorkType[]>([]);
  const [priorityLevels, setPriorityLevels] = useState<PriorityLevelList>();
  const [locations, setLocations] = useState<DBLocation[]>([]);
  const [serviceTypes, setServiceTypes] = useState<ServiceTypeList>();
  const [clients, setClients] = useState<Client[]>([]);
  const [lastWorkOrderNumber, setLastWorkOrderNumber] = useState<string | null>(null);
  const [highRiskClassifications, setHighRiskClassifications] = useState<HighRiskClassificationOption[]>([]);
  const [existingWorkOrders, setExistingWorkOrders] = useState<Ticket[]>([]);
  const [serviceTypeId, setServiceTypeId] = useState(defaultServiceType?.id);
  const [serviceTypeQuestions, setServiceTypeQuestions] = useState<IServiceTypeQuestion[]>([]);
  const { isShowing, toggle } = useModal();
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isFormLoading, setIsFormLoading] = useState(false);
  const [isVerifyMode, setIsVerifyMode] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    ticketName: "",
    description: "",
    serviceMode: "one_time",
    recurringSchedule: undefined,
    workRequestComment: "",
    requestorFirstName: "",
    requestorLastName: "",
    requestorEmail: "",
    requestorPhoneNumber: "",
    serviceType: defaultServiceType?.description || "",
    classification: "",
    priorityName: "",
    priority: 0,
    workType: "",
    location: "",
    targetStart: new Date(),
    targetFinish: new Date(),
    schedstart: "",
    schedfinish: "",
    actstart: "",
    actfinish: "",
  });
  const [files, setFiles] = useState<MaximoDocument[]>([]);
  const [classificationId, setClassificationId] = useState("");
  const [timezone, setTimezone] = useState("");
  const [customWorkRequestFields, setCustomWorkRequestFields] = useState<CustomWorkRequestFieldList>();
  const [isResourceAssignedModalVisible, setIsResourceAssignedModalVisible] = useState(false);
  const [assignResourceValues, setAssignResourceValues] = useState<AssignWOResourceFormValues>();
  const [pendingWonum, setPendingWonum] = useState<string | null>(null);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { targetFinish, targetStart, ...restFormData } = formData;

  const dropdownClassifications = useMemo(() => {
    return getDropdownClassification(
      !!isManager,
      serviceTypes,
      clientId,
      serviceTypeId,
      defaultServiceType,
      !!isNoAuthMode,
      noAuthData,
    );
  }, [defaultServiceType, serviceTypeId, clientId]);

  const buttonDisabledConditions = useMemo(() => getButtonDisableCondition(formData, serviceTypeQuestions), [formData]);

  const getAutoPurchaseOrderInput = (createdWorkOrder: { wonum: string; workorderid: string }) => {
    const vendorCost = Number(formData.vendorCost || formData.estimatedCost || 0);

    if (vendorCost <= 0) {
      return null;
    }

    const selectedClient = clients.find((client) => String(client.id) === String(clientId));
    const selectedLocation = locations.find((location) => String(location.id) === String(formData.location));
    const resolvedLocationName =
      selectedLocation?.label || selectedLocation?.address || String(formData.location || "");
    const resolvedShippingAddress =
      selectedLocation?.address || selectedLocation?.label || String(formData.location || "");

    const linkedVendorId = String(assignResourceValues?.vendors?.vendorId || "");
    const linkedVendor = (assignResourceValues as any)?.vendors?.vendorName || linkedVendorId;
    const fallbackApprover = [firstName, lastName].filter(Boolean).join(" ").trim();

    return {
      documentType: "purchase_order" as const,
      status: "draft" as const,
      companyName: selectedClient?.name || "",
      locationName: resolvedLocationName,
      billingAddress: selectedClient?.address || "",
      shippingAddress: resolvedShippingAddress,
      contact: {
        name: fallbackApprover,
        phone: phoneNumber || "",
        email: email || "",
      },
      vendorName: String(linkedVendor || ""),
      vendorAddress: "",
      vendorContactInfo: "",
      vendorId: linkedVendorId,
      lineItems: [
        {
          id: "1",
          description: formData.ticketName || formData.description || "Work request service",
          sku: "",
          quantity: 1,
          unitOfMeasure: "EA",
          unitPrice: vendorCost,
          lineTotal: vendorCost,
        },
      ],
      subtotal: vendorCost,
      tax: 0,
      shippingCost: 0,
      totalAmount: vendorCost,
      requestedDeliveryDate: formData.schedfinish || "",
      approverName: fallbackApprover,
      notes: formData.description || "Auto-generated from Billing and Procurement on work request creation.",
      linkedWorkOrderId: String(createdWorkOrder.workorderid),
      linkedWonum: createdWorkOrder.wonum,
      source: "work_request_auto" as const,
    };
  };

  const onSubmit = async (e: React.MouseEvent) => {
    setIsLoading(true);
    e.preventDefault();
    let workOrder;
    if (isManager) {
      // Compute dates at submission time (managers skip verify mode)
      const date = moment(new Date());
      const tZone = getTimeZone(clients, locations, location.pathname, maximoTimezone, clientId);
      if (tZone) date.tz(tZone);
      const finishDate = moment(date).add(formData.hoursDelay, "hours");
      const managerFormData = { ...formData, targetStart: date, targetFinish: finishDate };

      workOrder = onSubmitManagerForm(
        serviceTypes,
        clientId,
        dropdownClassifications,
        classificationId,
        defaultServiceType?.name,
        firstName,
        lastName,
        phoneNumber,
        timezone,
        email,
        managerFormData,
        maximoTimezone,
        serviceTypeQuestions,
        clients,
        locations,
        form,
        serviceTypeId,
      );
    }
    if (isClient) {
      workOrder = onSubmitClientForm(
        serviceTypes,
        clientId,
        dropdownClassifications,
        classificationId,
        defaultServiceType?.name,
        timezone,
        formData,
        maximoTimezone,
        serviceTypeQuestions,
        clients,
        locations,
        form,
        serviceTypeId,
      );
    }
    if (isNoAuthMode) {
      workOrder = onSubmitNoAuthForm(
        noAuthData?.serviceTypes?.serviceTypes as unknown as ServiceTypeItem[],
        clientId,
        dropdownClassifications,
        classificationId,
        defaultServiceType?.name,
        firstName,
        lastName,
        phoneNumber,
        email,
        timezone,
        formData,
        maximoTimezone,
        serviceTypeQuestions,
        clients,
        locations,
        form,
        serviceTypeId,
      );
    }

    const { data, message } = await FirebaseDataService.createMaximoWorkOrder(
      workOrder,
      clientUserId,
      assignResourceValues,
    );
    if (data) {
      if (formData.parentWorkOrder) {
        await FirebaseDataService.updateMaximoWorkOrderField(
          String(data.workorderid),
          "parent",
          formData.parentWorkOrder,
        );
      }

      const dateFieldUpdates = [
        { fieldName: "schedstart", value: formData.schedstart },
        { fieldName: "schedfinish", value: formData.schedfinish },
        { fieldName: "actstart", value: formData.actstart },
        { fieldName: "actfinish", value: formData.actfinish },
      ].filter((item) => !!item.value);

      if (dateFieldUpdates.length) {
        await Promise.all(
          dateFieldUpdates.map((item) =>
            FirebaseDataService.updateMaximoWorkOrderField(
              String(data.workorderid),
              item.fieldName,
              moment(item.value).tz(maximoTimezone, true).toISOString(),
            ),
          ),
        );
      }

      await uploadImages(String(data.workorderid), files);

      if (formData.workRequestComment?.trim()) {
        await saveCreateWorkOrderComment(String(data.workorderid), formData.workRequestComment, !!isManager);
      }

      const autoPoInput = getAutoPurchaseOrderInput({
        wonum: String(data.wonum),
        workorderid: String(data.workorderid),
      });

      if (autoPoInput) {
        try {
          await FirebaseDataService.ensurePurchaseOrderForWorkOrder(clientUserId, !!isManager, autoPoInput);
        } catch (autoPoError) {
          console.error("Failed to auto-create purchase order:", autoPoError);
        }
      }

      if (!isNoAuthMode) {
        dispatch(setWorkRequestMessage(data.message));

        if (formData.serviceMode === "recurring") {
          const recurringTab = WORK_REQUEST_TYPE.PREVENTATIVE_MAINTENANCE;
          const searchParams = new URLSearchParams({
            activeTab: recurringTab,
            [`${recurringTab}_PAGE_NUMBER`]: "1",
          });

          navigate(`/workrequests?${searchParams.toString()}`);
        } else if (isManager) {
          // Send-out-work popup is temporarily disabled.
          // Previous behavior:
          // setPendingWonum(data.wonum);
          navigate(`/workrequests/${data.wonum}`);
        } else {
          // Auto-open send-out-work modal is temporarily disabled.
          // Previous behavior:
          // navigate(`/workrequests/${data.wonum}`, {
          //   state: {
          //     openIssueWorkOrderModal: true,
          //   },
          // });
          navigate(`/workrequests/${data.wonum}`);
        }
      } else {
        setStep?.((old: number) => old + 1);
        setSuccessMessage?.(data.message);
      }
    }

    if (!data) {
      setError(message);
    }

    setIsLoading(false);
    clearWorkRequestMessageTimeout(dispatch);
  };

  const onConfirmClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    if (await form.trigger()) {
      if (isManager) {
        await onSubmit(event as React.MouseEvent);
      } else {
        setIsVerifyMode(true);
      }
    }
  };

  const onExitClick = () => {
    toggle();
  };

  useEffect(() => {
    fetchData(
      setIsFormLoading,
      !!isNoAuthMode,
      noAuthData,
      setWorkTypes,
      setPriorityLevels,
      setLocations,
      setCustomWorkRequestFields,
      id,
      clientUserId,
      !!isManager,
      setServiceTypes,
      setClients,
      setLastWorkOrderNumber,
      setHighRiskClassifications,
    );
  }, [id]);

  useEffect(() => {
    if (!isManager || !clientUserId) return;

    const fetchExistingWorkOrders = async () => {
      try {
        const bigPage = { pageNum: 1, pageSize: 200 };

        const [open, approval] = await Promise.all([
          FirebaseDataService.getMaximoWorkOrders(clientUserId, { ...bigPage, workRequestType: "OPEN" }, true, ""),
          FirebaseDataService.getMaximoWorkOrders(
            clientUserId,
            { ...bigPage, workRequestType: "APPROVAL_REQUIRED" },
            true,
            "",
          ),
        ]);

        const combined = [...(open.data?.workOrders ?? []), ...(approval.data?.workOrders ?? [])];
        const seen = new Set<string>();
        setExistingWorkOrders(
          combined.filter((workOrder) => {
            if (seen.has(workOrder.wonum)) return false;
            seen.add(workOrder.wonum);
            return true;
          }),
        );
      } catch {
        setExistingWorkOrders([]);
      }
    };

    fetchExistingWorkOrders();
  }, [clientUserId, isManager]);

  useEffect(() => {
    setTimezone(getTimeZone(clients, locations, location.pathname, maximoTimezone, clientId));
  }, [clientId, formData.location]);
  useEffect(() => {
    // set target start and taget finish for create new ticket
    if (isVerifyMode) {
      const date = moment(new Date());
      const tZone = getTimeZone(clients, locations, location.pathname, maximoTimezone, clientId);
      if (tZone) {
        date.tz(tZone);
      }

      const finishDate = moment(date).add(formData.hoursDelay, "hours");

      setFormData((data) => ({
        ...data,
        targetStart: date,
        targetFinish: finishDate,
      }));
    }
  }, [isVerifyMode]);

  useEffect(() => {
    serviceTypeId && getServiceTypeQuestions(clientId, serviceTypeId, setFormData, setServiceTypeQuestions, setError);
  }, [serviceTypeId]);

  useEffect(() => {
    if (!priorityLevels) return;
    const dataSource = (isManager ? priorityLevels[clientId] : priorityLevels) as PriorityLevel[];

    if (dataSource?.length === 1) {
      setFormData((data) => ({
        ...data,
        priorityName: dataSource[0].name,
        priority: dataSource[0].priority,
        hoursDelay: dataSource[0].hoursDelay,
      }));
    }
  }, [priorityLevels]);

  useEffect(() => {
    if (isManager) {
      setFormData((data) => ({
        // Keep location when it belongs to the selected client (e.g. modal picks both at once).
        // Otherwise clear it when switching to a different client.
        ...data,
        location: locations.some(
          (location) => String(location.clientId) === String(clientId) && String(location.id) === String(data.location),
        )
          ? data.location
          : "",
        serviceType: "",
        priority: 0,
        priorityName: "",
        classification: "",
        parentWorkOrder: "",
        workRequestComment: "",
      }));
      setServiceTypeId("");
      setServiceTypeQuestions([]);
    }
  }, [clientId, isManager, locations]);

  useEffect(() => {
    dispatch(setPageNameHeader("Create New Work Request"));
    return () => {
      dispatch(setPageNameHeader(""));
    };
  }, []);

  const getWorkRequestForm = () => {
    if (isManager) {
      return (
        <ManagerCreateFormFields
          clientId={clientId}
          clients={clients}
          customWorkRequestFields={customWorkRequestFields}
          files={files}
          form={form}
          formData={formData}
          isVerifyMode={isVerifyMode}
          locations={locations}
          maximoTimezone={maximoTimezone}
          onExitClick={onExitClick}
          serviceTypeId={serviceTypeId}
          serviceTypeQuestions={serviceTypeQuestions}
          serviceTypes={serviceTypes}
          setClassificationId={setClassificationId}
          setClientId={setClientId}
          setFiles={setFiles}
          setFormData={setFormData}
          setIsResourceAssignedModalVisible={setIsResourceAssignedModalVisible}
          setAssignResourceValues={setAssignResourceValues}
          setServiceTypeId={setServiceTypeId}
          targetFinish={targetFinish}
          targetStart={targetStart}
          timezone={timezone}
          workTypes={workTypes}
          assignResourceValues={assignResourceValues}
          priorityLevels={priorityLevels}
          error={error}
          buttonDisabledConditions={buttonDisabledConditions}
          isLoading={isLoading}
          onConfirmClick={onConfirmClick}
          onSubmit={onSubmit}
          setIsVerifyMode={setIsVerifyMode}
          lastWorkOrderNumber={lastWorkOrderNumber}
          existingWorkOrders={existingWorkOrders}
          highRiskClassifications={highRiskClassifications}
        />
      );
    }
    if (isNoAuthMode) {
      return (
        <NoAuthCreateFormFields
          clientId={clientId}
          files={files}
          form={form}
          formData={formData}
          isVerifyMode={isVerifyMode}
          locations={locations}
          maximoTimezone={maximoTimezone}
          serviceTypeId={serviceTypeId}
          serviceTypeQuestions={serviceTypeQuestions}
          setClassificationId={setClassificationId}
          setFiles={setFiles}
          setFormData={setFormData}
          setServiceTypeId={setServiceTypeId}
          targetFinish={targetFinish}
          targetStart={targetStart}
          timezone={timezone}
          workTypes={workTypes}
          priorityLevels={priorityLevels}
          error={error}
          buttonDisabledConditions={buttonDisabledConditions}
          isLoading={isLoading}
          onConfirmClick={onConfirmClick}
          onSubmit={onSubmit}
          setIsVerifyMode={setIsVerifyMode}
          customWorkRequestFields={customWorkRequestFields}
          serviceTypes={serviceTypes}
          setTimezone={setTimezone}
          noAuthData={noAuthData}
        />
      );
    }
    if (isClient) {
      const scopedClients = clients.filter((client) => String(client.id) === String(clientId));

      return (
        <ManagerCreateFormFields
          clientId={clientId}
          clients={scopedClients}
          files={files}
          form={form}
          formData={formData}
          isVerifyMode={isVerifyMode}
          locations={locations}
          maximoTimezone={maximoTimezone}
          onExitClick={onExitClick}
          serviceTypeId={serviceTypeId}
          serviceTypeQuestions={serviceTypeQuestions}
          serviceTypes={serviceTypes}
          setClassificationId={setClassificationId}
          setClientId={setClientId}
          setFiles={setFiles}
          setFormData={setFormData}
          setIsResourceAssignedModalVisible={setIsResourceAssignedModalVisible}
          setAssignResourceValues={setAssignResourceValues}
          setServiceTypeId={setServiceTypeId}
          targetFinish={targetFinish}
          targetStart={targetStart}
          timezone={timezone}
          workTypes={workTypes}
          priorityLevels={priorityLevels}
          error={error}
          buttonDisabledConditions={buttonDisabledConditions}
          isLoading={isLoading}
          onConfirmClick={onConfirmClick}
          onSubmit={onSubmit}
          setIsVerifyMode={setIsVerifyMode}
          customWorkRequestFields={customWorkRequestFields}
          assignResourceValues={assignResourceValues}
          lastWorkOrderNumber={lastWorkOrderNumber}
          existingWorkOrders={existingWorkOrders}
          highRiskClassifications={highRiskClassifications}
        />
      );
    }
  };
  return (
    <LoadingContainer isLoading={isFormLoading}>
      <WorkRequestModals
        clientId={clientId}
        isResourceAssignedModalVisible={isResourceAssignedModalVisible}
        setAssignResourceValues={setAssignResourceValues}
        setIsResourceAssignedModalVisible={setIsResourceAssignedModalVisible}
        assignResourceValues={assignResourceValues}
        isShowing={isShowing}
        toggle={toggle}
      />

      {pendingWonum && (
        <SendWorkOverlay>
          <SendWorkCard>
            <SendWorkTitle>Work Order Created</SendWorkTitle>
            <SendWorkMessage>
              Work order <strong>{pendingWonum}</strong> has been created successfully. Would you like to send this work
              order out?
            </SendWorkMessage>
            <SendWorkActions>
              <SendWorkNoButton
                type="button"
                onClick={() => {
                  setPendingWonum(null);
                  navigate("/workrequests");
                }}
              >
                No
              </SendWorkNoButton>
              <SendWorkYesButton
                type="button"
                onClick={() => {
                  const wonum = pendingWonum;
                  setPendingWonum(null);
                  navigate(`/workrequests/${wonum}`, {
                    state: { openIssueWorkOrderModal: true },
                  });
                }}
              >
                Yes
              </SendWorkYesButton>
            </SendWorkActions>
          </SendWorkCard>
        </SendWorkOverlay>
      )}

      {getWorkRequestForm()}
    </LoadingContainer>
  );
};

const SendWorkOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 10010;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

const SendWorkCard = styled.div`
  background: #ffffff;
  border: 1px solid #c9d2dc;
  width: min(480px, 100%);
  padding: 32px;
`;

const SendWorkTitle = styled.h3`
  margin: 0 0 12px;
  font-size: 20px;
  font-weight: 600;
  color: #1f2a37;
`;

const SendWorkMessage = styled.p`
  margin: 0 0 28px;
  font-size: 14px;
  font-weight: 300;
  color: #1f2a37;
  line-height: 1.6;
`;

const SendWorkActions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`;

const SendWorkNoButton = styled.button`
  border: 1px solid #c9d2dc;
  background: #ffffff;
  color: #1f2a37;
  padding: 12px 24px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  cursor: pointer;

  &:hover {
    border-color: #1f2a37;
  }
`;

const SendWorkYesButton = styled.button`
  border: 1px solid #1f2a37;
  background: #1f2a37;
  color: #ffffff;
  padding: 12px 24px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  cursor: pointer;

  &:hover {
    background: #374151;
    border-color: #374151;
  }
`;
